import { reportError } from 'shared/services/raygunService';
import { useContext } from 'react';
import { UserDataContext } from 'shared/context/userDataContext';
import { usePortalApi } from 'shared/const/api-backend';

const useAgencyService = () => {
  const api = usePortalApi();
  const { userData } = useContext(UserDataContext);
  const userCountry = userData?.ssoData?.businessCountryCode || 'default';

  const getAgencies = async () => {
    try {
      const response = await api.get(`proxy/v1/agency?country=${userCountry}`);
      return response.data.data;
    } catch (error) {
      reportError(error);

      throw new Error('Failed to fetch agencies.');
    }
  };

  const getAgency = async (id) => {
    try {
      const response = await api.get(
        `proxy/v1/agency/${id}?country=${userCountry}`,
      );
      return response.data.data;
    } catch (error) {
      reportError(error);

      throw new Error(`Failed to fetch agency with ID: ${id}.`);
    }
  };

  const createAgency = async (agencyData) => {
    try {
      const response = await api.post(
        `proxy/v1/agency?country=${userCountry}`,
        agencyData,
      );

      return response.data.data;
    } catch (error) {
      reportError(error);

      throw new Error('Failed to create new agency.');
    }
  };

  const deleteAgency = async (id) => {
    try {
      const response = await api.delete(
        `proxy/v1/agency/${id}?country=${userCountry}`,
      );

      return response.data.data;
    } catch (error) {
      reportError(error);

      throw new Error(`Failed to delete agency with ID: ${id}.`);
    }
  };

  const updateAgency = async (id, agent) => {
    try {
      const response = await api.patch(
        `proxy/v1/agency/${id}?country=${userCountry}`,
        agent,
      );

      return response.data.data;
    } catch (error) {
      reportError(error);

      throw new Error(`Failed to update agency with ID: ${id}.`);
    }
  };

  const createAgencyNote = async (id, noteText) => {
    const note = {
      noteText,
      noteDate: new Date().toISOString(),
    };

    try {
      const response = await api.post(
        `proxy/v1/agency/${id}/note?country=${userCountry}`,
        note,
      );

      return response.data.data;
    } catch (error) {
      reportError(error);

      throw new Error(`Failed to create agency note for ID: ${id}.`);
    }
  };

  const getAgencyNotes = async (id) => {
    try {
      const response = await api.get(
        `proxy/v1/agency/${id}/notes?country=${userCountry}`,
      );

      return response.data.data;
    } catch (error) {
      reportError(error);

      throw new Error(`Failed to get agency notes for ID: ${id}.`);
    }
  };

  return {
    getAgencies,
    getAgency,
    createAgency,
    deleteAgency,
    updateAgency,
    createAgencyNote,
    getAgencyNotes,
  };
};

export default useAgencyService;
